/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Optional,
    Output,
    ViewContainerRef,
} from '@angular/core';
import {MdMenuPanel} from './menu-panel';
import {throwMdMenuMissingError} from './menu-errors';
import {
    isFakeMousedownFromScreenReader,
    Dir,
    LayoutDirection,
    Overlay,
    OverlayState,
    OverlayRef,
    TemplatePortal,
    ConnectedPositionStrategy,
    HorizontalConnectionPos,
    VerticalConnectionPos,
} from '../core';
import {Subscription} from 'rxjs/Subscription';
import {MenuPositionX, MenuPositionY} from './menu-positions';
/**
 * This directive is intended to be used in conjunction with an md-menu tag.  It is
 * responsible for toggling the display of the provided menu instance.
 */
export class MdMenuTrigger implements AfterViewInit, OnDestroy {
private _portal: TemplatePortal;
private _overlayRef: OverlayRef;
private _menuOpen: boolean = false;
private _backdropSubscription: Subscription;
private _positionSubscription: Subscription;
private _openedByMouse: boolean = false;
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedMdMenuTriggerFor(): MdMenuPanel { return this.menu; }
/**
 * @param {?} v
 * @return {?}
 */
set _deprecatedMdMenuTriggerFor(v: MdMenuPanel) { this.menu = v; }
/**
 * @deprecated
 * @return {?}
 */
get _deprecatedMatMenuTriggerFor(): MdMenuPanel { return this.menu; }
/**
 * @param {?} v
 * @return {?}
 */
set _deprecatedMatMenuTriggerFor(v: MdMenuPanel) { this.menu = v; }
/**
 * @return {?}
 */
get _matMenuTriggerFor(): MdMenuPanel { return this.menu; }
/**
 * @param {?} v
 * @return {?}
 */
set _matMenuTriggerFor(v: MdMenuPanel) { this.menu = v; }
/**
 * References the menu instance that the trigger is associated with.
 */
menu: MdMenuPanel;
/**
 * Event emitted when the associated menu is opened.
 */
onMenuOpen = new EventEmitter<void>();
/**
 * Event emitted when the associated menu is closed.
 */
onMenuClose = new EventEmitter<void>();
/**
 * @param {?} _overlay
 * @param {?} _element
 * @param {?} _viewContainerRef
 * @param {?} _dir
 */
constructor(private _overlay: Overlay,
private _element: ElementRef,
private _viewContainerRef: ViewContainerRef,
private _dir: Dir) { }
/**
 * @return {?}
 */
ngAfterViewInit() {
    this._checkMenu();
    this.menu.close.subscribe(() => this.closeMenu());
  }
/**
 * @return {?}
 */
ngOnDestroy() { this.destroyMenu(); }
/**
 * Whether the menu is open.
 * @return {?}
 */
get menuOpen(): boolean { return this._menuOpen; }
/**
 * Toggles the menu between the open and closed states.
 * @return {?}
 */
toggleMenu(): void {
    return this._menuOpen ? this.closeMenu() : this.openMenu();
  }
/**
 * Opens the menu.
 * @return {?}
 */
openMenu(): void {
    if (!this._menuOpen) {
      this._createOverlay();
      this._overlayRef.attach(this._portal);
      this._subscribeToBackdrop();
      this._initMenu();
    }
  }
/**
 * Closes the menu.
 * @return {?}
 */
closeMenu(): void {
    if (this._overlayRef) {
      this._overlayRef.detach();
      this._backdropSubscription.unsubscribe();
      this._resetMenu();
    }
  }
/**
 * Removes the menu from the DOM.
 * @return {?}
 */
destroyMenu(): void {
    if (this._overlayRef) {
      this._overlayRef.dispose();
      this._overlayRef = null;

      this._cleanUpSubscriptions();
    }
  }
/**
 * Focuses the menu trigger.
 * @return {?}
 */
focus() {
    this._element.nativeElement.focus();
  }
/**
 * The text direction of the containing app.
 * @return {?}
 */
get dir(): LayoutDirection {
    return this._dir && this._dir.value === 'rtl' ? 'rtl' : 'ltr';
  }
/**
 * This method ensures that the menu closes when the overlay backdrop is clicked.
 * We do not use first() here because doing so would not catch clicks from within
 * the menu, and it would fail to unsubscribe properly. Instead, we unsubscribe
 * explicitly when the menu is closed or destroyed.
 * @return {?}
 */
private _subscribeToBackdrop(): void {
    this._backdropSubscription = this._overlayRef.backdropClick().subscribe(() => {
      this.menu._emitCloseEvent();
    });
  }
/**
 * This method sets the menu state to open and focuses the first item if
 * the menu was opened via the keyboard.
 * @return {?}
 */
private _initMenu(): void {
    this._setIsMenuOpen(true);

    // Should only set focus if opened via the keyboard, so keyboard users can
    // can easily navigate menu items. According to spec, mouse users should not
    // see the focus style.
    if (!this._openedByMouse) {
      this.menu.focusFirstItem();
    }
  }
/**
 * This method resets the menu when it's closed, most importantly restoring
 * focus to the menu trigger if the menu was opened via the keyboard.
 * @return {?}
 */
private _resetMenu(): void {
    this._setIsMenuOpen(false);

    // Focus only needs to be reset to the host element if the menu was opened
    // by the keyboard and manually shifted to the first menu item.
    if (!this._openedByMouse) {
      this.focus();
    }
    this._openedByMouse = false;
  }
/**
 * @param {?} isOpen
 * @return {?}
 */
private _setIsMenuOpen(isOpen: boolean): void {
    this._menuOpen = isOpen;
    this._menuOpen ? this.onMenuOpen.emit() : this.onMenuClose.emit();
  }
/**
 *  This method checks that a valid instance of MdMenu has been passed into
 *  mdMenuTriggerFor. If not, an exception is thrown.
 * @return {?}
 */
private _checkMenu() {
    if (!this.menu) {
      throwMdMenuMissingError();
    }
  }
/**
 *  This method creates the overlay from the provided menu's template and saves its
 *  OverlayRef so that it can be attached to the DOM when openMenu is called.
 * @return {?}
 */
private _createOverlay(): void {
    if (!this._overlayRef) {
      this._portal = new TemplatePortal(this.menu.templateRef, this._viewContainerRef);
      const /** @type {?} */ config = this._getOverlayConfig();
      this._subscribeToPositions( /** @type {?} */((config.positionStrategy as ConnectedPositionStrategy)));
      this._overlayRef = this._overlay.create(config);
    }
  }
/**
 * This method builds the configuration object needed to create the overlay, the OverlayState.
 * @return {?} OverlayState
 */
private _getOverlayConfig(): OverlayState {
    const /** @type {?} */ overlayState = new OverlayState();
    overlayState.positionStrategy = this._getPosition()
                                        .withDirection(this.dir);
    overlayState.hasBackdrop = true;
    overlayState.backdropClass = 'cdk-overlay-transparent-backdrop';
    overlayState.direction = this.dir;
    overlayState.scrollStrategy = this._overlay.scrollStrategies.reposition();
    return overlayState;
  }
/**
 * Listens to changes in the position of the overlay and sets the correct classes
 * on the menu based on the new position. This ensures the animation origin is always
 * correct, even if a fallback position is used for the overlay.
 * @param {?} position
 * @return {?}
 */
private _subscribeToPositions(position: ConnectedPositionStrategy): void {
    this._positionSubscription = position.onPositionChange.subscribe((change) => {
      const /** @type {?} */ posX: MenuPositionX = change.connectionPair.originX === 'start' ? 'after' : 'before';
      let /** @type {?} */ posY: MenuPositionY = change.connectionPair.originY === 'top' ? 'below' : 'above';

      if (!this.menu.overlapTrigger) {
        posY = posY === 'below' ? 'above' : 'below';
      }

      this.menu.setPositionClasses(posX, posY);
    });
  }
/**
 * This method builds the position strategy for the overlay, so the menu is properly connected
 * to the trigger.
 * @return {?} ConnectedPositionStrategy
 */
private _getPosition(): ConnectedPositionStrategy  {
    const [posX, fallbackX]: HorizontalConnectionPos[] =
      this.menu.xPosition === 'before' ? ['end', 'start'] : ['start', 'end'];

    const [overlayY, fallbackOverlayY]: VerticalConnectionPos[] =
      this.menu.yPosition === 'above' ? ['bottom', 'top'] : ['top', 'bottom'];

    let /** @type {?} */ originY = overlayY;
    let /** @type {?} */ fallbackOriginY = fallbackOverlayY;

    if (!this.menu.overlapTrigger) {
      originY = overlayY === 'top' ? 'bottom' : 'top';
      fallbackOriginY = fallbackOverlayY === 'top' ? 'bottom' : 'top';
    }

    return this._overlay.position()
      .connectedTo(this._element,
          {originX: posX, originY: originY}, {overlayX: posX, overlayY: overlayY})
      .withFallbackPosition(
          {originX: fallbackX, originY: originY},
          {overlayX: fallbackX, overlayY: overlayY})
      .withFallbackPosition(
          {originX: posX, originY: fallbackOriginY},
          {overlayX: posX, overlayY: fallbackOverlayY})
      .withFallbackPosition(
          {originX: fallbackX, originY: fallbackOriginY},
          {overlayX: fallbackX, overlayY: fallbackOverlayY});
  }
/**
 * @return {?}
 */
private _cleanUpSubscriptions(): void {
    if (this._backdropSubscription) {
      this._backdropSubscription.unsubscribe();
    }
    if (this._positionSubscription) {
      this._positionSubscription.unsubscribe();
    }
  }
/**
 * @param {?} event
 * @return {?}
 */
_handleMousedown(event: MouseEvent): void {
    if (!isFakeMousedownFromScreenReader(event)) {
      this._openedByMouse = true;
    }
  }

static decorators: DecoratorInvocation[] = [
{ type: Directive, args: [{
  selector: `[md-menu-trigger-for], [mat-menu-trigger-for],
             [mdMenuTriggerFor], [matMenuTriggerFor]`,
  host: {
    'aria-haspopup': 'true',
    '(mousedown)': '_handleMousedown($event)',
    '(click)': 'toggleMenu()',
  },
  exportAs: 'mdMenuTrigger'
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Overlay, },
{type: ElementRef, },
{type: ViewContainerRef, },
{type: Dir, decorators: [{ type: Optional }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'_deprecatedMdMenuTriggerFor': [{ type: Input, args: ['md-menu-trigger-for', ] },],
'_deprecatedMatMenuTriggerFor': [{ type: Input, args: ['mat-menu-trigger-for', ] },],
'_matMenuTriggerFor': [{ type: Input, args: ['matMenuTriggerFor', ] },],
'menu': [{ type: Input, args: ['mdMenuTriggerFor', ] },],
'onMenuOpen': [{ type: Output },],
'onMenuClose': [{ type: Output },],
};
}

function MdMenuTrigger_tsickle_Closure_declarations() {
/** @type {?} */
MdMenuTrigger.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdMenuTrigger.ctorParameters;
/** @type {?} */
MdMenuTrigger.propDecorators;
/** @type {?} */
MdMenuTrigger.prototype._portal;
/** @type {?} */
MdMenuTrigger.prototype._overlayRef;
/** @type {?} */
MdMenuTrigger.prototype._menuOpen;
/** @type {?} */
MdMenuTrigger.prototype._backdropSubscription;
/** @type {?} */
MdMenuTrigger.prototype._positionSubscription;
/** @type {?} */
MdMenuTrigger.prototype._openedByMouse;
/**
 * References the menu instance that the trigger is associated with.
 * @type {?}
 */
MdMenuTrigger.prototype.menu;
/**
 * Event emitted when the associated menu is opened.
 * @type {?}
 */
MdMenuTrigger.prototype.onMenuOpen;
/**
 * Event emitted when the associated menu is closed.
 * @type {?}
 */
MdMenuTrigger.prototype.onMenuClose;
/** @type {?} */
MdMenuTrigger.prototype._overlay;
/** @type {?} */
MdMenuTrigger.prototype._element;
/** @type {?} */
MdMenuTrigger.prototype._viewContainerRef;
/** @type {?} */
MdMenuTrigger.prototype._dir;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
